import { useQuery } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

async function fetchJson<T>(path: string, headers: Record<string, string>): Promise<T> {
  const res = await fetch(path, { headers });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${path}: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export interface HomeMetrics {
  conversion_mtd: {
    rate: number;
    target: number;
    historical_rolling_avg_6mo: number;
    won_count: number;
    total_count: number;
    won_revenue_usd: number;
  };
  quotes_converted_via_relance_mtd: {
    count: number;
    revenue_usd: number;
  };
  relance_assisted_schedule: {
    lifetime: {
      count: number;
      revenue_usd: number;
      measured_since: string | null;
    };
    mtd: {
      count: number;
      revenue_usd: number;
    };
    monthly: Array<{
      month: string;
      count: number;
      revenue_usd: number;
    }>;
  };
  best_performing_touch: {
    campaign: string;
    display_name: string;
    conversion_rate: number;
    touched: number;
    won: number;
    revenue_attributable: number;
  } | null;
  five_star_reviews_via_agent_mtd: number;
  five_star_reviews_via_agent_lifetime: number;
  generated_at: string;
}

export function useHomeMetrics() {
  const headers = useAuthHeaders();
  return useQuery<HomeMetrics>({
    queryKey: ["control-tower", "home", "metrics"],
    queryFn: () => fetchJson<HomeMetrics>("/api/cockpit/home/metrics", headers),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

export type HomeAlert = {
  type: string;
  severity: "critical" | "warning" | "info";
  customer?: string;
  amount?: number;
  excerpt?: string;
  rate?: number;
  message?: string;
  quote_id?: string;
  reminder_id?: number;
  ts?: string;
};

export function useHomeAlerts() {
  const headers = useAuthHeaders();
  return useQuery<{ alerts: HomeAlert[]; total: number; generated_at: string }>({
    queryKey: ["control-tower", "home", "alerts"],
    queryFn: () => fetchJson("/api/cockpit/home/alerts", headers),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
